import React from "react";
import { Link } from "react-router-dom";

const sectionClass = "space-y-3";
const h2Class = "text-xl font-semibold text-white scroll-mt-24";
const pClass = "text-sm leading-relaxed text-gray-300";
const ulClass = "list-disc space-y-2 pl-5 text-sm leading-relaxed text-gray-300";

const PrivacyPolicyPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-950 text-gray-300">
      <header className="sticky top-0 z-50 border-b border-gray-800 bg-gray-950/95 backdrop-blur supports-[backdrop-filter]:bg-gray-950/80">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-4 py-3 sm:px-6">
          <span className="text-lg font-semibold tracking-tight text-white">IntelliWarrior</span>
          <Link
            to="/"
            className="text-sm font-medium text-emerald-400 transition-colors hover:text-emerald-300"
          >
            Back to Home
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-10 pb-16 sm:px-6">
        <div className="mb-10 border-b border-gray-800 pb-8">
          <h1 className="text-3xl font-bold text-white">Privacy Policy</h1>
          <p className="mt-2 text-sm text-gray-400">
            This Privacy Policy describes how Warrior Financial Services LLC (&quot;we,&quot;
            &quot;us,&quot; or &quot;our&quot;) collects, uses, discloses, and protects information in
            connection with IntelliWarrior (the &quot;Service&quot;).
          </p>
          <p className="mt-2 text-xs text-gray-500">Last updated: April 1, 2026</p>
        </div>

        <div className="space-y-10">
          <section id="intro" className={sectionClass}>
            <h2 className={h2Class}>1. Introduction</h2>
            <p className={pClass}>
              We respect your privacy. This policy applies to visitors to our marketing website, Operators
              who create accounts to use the platform, and information about end clients that Operators enter
              or upload into the Service. By using the Service, you agree to this Privacy Policy together
              with our{" "}
              <Link to="/terms" className="text-emerald-400 hover:underline">
                Terms of Service
              </Link>
              .
            </p>
          </section>

          <section id="collect" className={sectionClass}>
            <h2 className={h2Class}>2. Information We Collect</h2>
            <p className={pClass}>We may collect the following categories of information:</p>
            <ul className={ulClass}>
              <li>
                <strong className="text-gray-200">Operator accounts:</strong> Name, business name, email
                address, phone number, billing and payment details (processed by our payment provider),
                credentials, and settings you configure in the Service.
              </li>
              <li>
                <strong className="text-gray-200">Client data entered by Operators:</strong> Information
                Operators add about their clients (for example, contact details, dispute items, notes, and
                other fields available in the product). Operators control what they enter.
              </li>
              <li>
                <strong className="text-gray-200">Automatically collected information:</strong> Log data,
                device and browser type, IP address, approximate location derived from IP, timestamps, pages
                or features accessed, and diagnostic data to operate and secure the Service.
              </li>
              <li>
                <strong className="text-gray-200">Uploaded documents:</strong> Files Operators or authorized
                users upload (for example, identification, credit reports, letters, or other documents)
                stored to provide document management features.
              </li>
            </ul>
          </section>

          <section id="use" className={sectionClass}>
            <h2 className={h2Class}>3. How We Use Information</h2>
            <p className={pClass}>We use information to:</p>
            <ul className={ulClass}>
              <li>Provide, maintain, and improve the Service and its features;</li>
              <li>Authenticate users, prevent fraud and abuse, and protect security;</li>
              <li>Process subscriptions and payments and send transactional communications;</li>
              <li>Provide customer support and respond to inquiries;</li>
              <li>Comply with legal obligations and enforce our terms; and</li>
              <li>Analyze usage in aggregate or de-identified form to improve the product.</li>
            </ul>
          </section>

          <section id="sharing" className={sectionClass}>
            <h2 className={h2Class}>4. Data Sharing</h2>
            <p className={pClass}>
              We share information with service providers who assist us in operating the Service, subject to
              appropriate safeguards:
            </p>
            <ul className={ulClass}>
              <li>
                <strong className="text-gray-200">Stripe</strong> (or similar payment processors) for
                subscription billing and payment processing.
              </li>
              <li>
                <strong className="text-gray-200">SendGrid</strong> (or similar email providers) for
                transactional and service-related email.
              </li>
              <li>
                <strong className="text-gray-200">DigitalOcean</strong> (or similar cloud hosting providers)
                for hosting, storage, and infrastructure.
              </li>
              <li>
                <strong className="text-gray-200">AI processing:</strong> When you use AI-powered features,
                relevant content may be sent to our AI vendors to generate responses. We configure services so
                that your data is not used to train third-party foundation models, except as we may disclose
                separately if our vendor practices change. AI subprocessors are engaged to provide the
                feature you request, not for unrelated marketing.
              </li>
            </ul>
            <p className={pClass}>
              We may also disclose information if required by law, to protect rights and safety, or in
              connection with a merger, acquisition, or asset sale, as permitted by law.
            </p>
          </section>

          <section id="multitenant" className={sectionClass}>
            <h2 className={h2Class}>5. Multi-Tenant Data Isolation</h2>
            <p className={pClass}>
              IntelliWarrior is designed as a multi-tenant application. We use technical and organizational
              measures so that each Operator&apos;s workspace data is logically separated and accessible
              only to authorized users for that tenant, according to role and permissions. You should still
              protect your account credentials and only invite trusted team members.
            </p>
          </section>

          <section id="security" className={sectionClass}>
            <h2 className={h2Class}>6. Data Security</h2>
            <p className={pClass}>
              We implement reasonable administrative, technical, and physical safeguards appropriate to the
              nature of the Service, including:
            </p>
            <ul className={ulClass}>
              <li>Encryption in transit (TLS) for data transmitted between your browser and our services;</li>
              <li>Industry-standard password hashing (e.g., BCrypt) for stored credentials where applicable;</li>
              <li>Role-based access controls limiting staff and system access to production data; and</li>
              <li>Monitoring and logging to detect and respond to security issues.</li>
            </ul>
            <p className={pClass}>
              No method of transmission or storage is completely secure. You use the Service at your own
              risk to the extent permitted by law.
            </p>
          </section>

          <section id="retention" className={sectionClass}>
            <h2 className={h2Class}>7. Data Retention</h2>
            <p className={pClass}>
              We retain information for as long as needed to provide the Service, comply with law, resolve
              disputes, and enforce our agreements. Unless a longer period is required by law or a specific
              agreement with you, we generally delete or de-identify tenant operational data within{" "}
              <strong className="text-gray-200">ninety (90) days</strong> after account cancellation or
              termination, subject to backup and archival cycles. Some records (such as billing and audit logs)
              may be retained longer where legally required or for legitimate business purposes.
            </p>
          </section>

          <section id="controllers" className={sectionClass}>
            <h2 className={h2Class}>8. Operator Responsibilities as Data Controllers</h2>
            <p className={pClass}>
              Where Operators process personal information about their own clients in the Service, Operators
              typically act as data controllers (or equivalent) for that client data, and we act as a
              processor or service provider on their behalf. Operators are responsible for obtaining lawful
              bases and consents, providing required notices to their clients, honoring client rights to the
              extent applicable, and for the accuracy and legality of the data they submit.
            </p>
          </section>

          <section id="cookies" className={sectionClass}>
            <h2 className={h2Class}>9. Cookies and Tracking</h2>
            <p className={pClass}>
              We use cookies and similar technologies that are <strong className="text-gray-200">essential</strong>{" "}
              to operate the Service—for example, to keep you signed in and maintain security. We do not use
              advertising or third-party ad tracking cookies for cross-site behavioral ads as part of the
              Service. If we introduce optional analytics cookies in the future, we will update this policy
              and provide appropriate choices where required.
            </p>
          </section>

          <section id="rights" className={sectionClass}>
            <h2 className={h2Class}>10. Your Rights</h2>
            <p className={pClass}>
              Depending on where you live, you may have rights to access, correct, delete, or export
              personal information, or to object to or restrict certain processing. Operators may exercise
              many of these rights directly in account settings or by contacting us. End clients of an
              Operator should contact that Operator first; we will assist the Operator as required by our
              agreement and applicable law.
            </p>
            <p className={pClass}>
              To submit a request, email{" "}
              <a href="mailto:info@intelliwarrior.com" className="text-emerald-400 hover:underline">
                info@intelliwarrior.com
              </a>
              . We may need to verify your identity before fulfilling requests.
            </p>
          </section>

          <section id="children" className={sectionClass}>
            <h2 className={h2Class}>11. Children&apos;s Privacy</h2>
            <p className={pClass}>
              The Service is not directed to children under thirteen (13) years of age (or the minimum age in
              your jurisdiction). We do not knowingly collect personal information from children. If you
              believe we have collected information from a child, contact us and we will take appropriate
              steps to delete it.
            </p>
          </section>

          <section id="changes" className={sectionClass}>
            <h2 className={h2Class}>12. Changes to This Policy</h2>
            <p className={pClass}>
              We may update this Privacy Policy from time to time. We will post the revised policy on this
              page and update the &quot;Last updated&quot; date. For material changes, we may provide
              additional notice (for example, by email or in-product message). Your continued use of the
              Service after the effective date constitutes acceptance of the updated policy where permitted by
              law.
            </p>
          </section>

          <section id="contact" className={sectionClass}>
            <h2 className={h2Class}>13. Contact</h2>
            <p className={pClass}>
              Warrior Financial Services LLC — Privacy inquiries:{" "}
              <a href="mailto:info@intelliwarrior.com" className="text-emerald-400 hover:underline">
                info@intelliwarrior.com
              </a>
            </p>
          </section>
        </div>

        <footer className="mt-16 border-t border-gray-800 pt-8 text-center text-xs text-gray-500">
          © {new Date().getFullYear()} Warrior Financial Services LLC. All rights reserved.
        </footer>
      </main>
    </div>
  );
};

export default PrivacyPolicyPage;
